import Fuse, { type IFuseOptions } from "fuse.js";
import { meaningfulTokens, toWords, tokenMatches } from "./match";
import type { PaletteItem } from "./types";

/** Max rows per group in a SEARCH result, so one group cannot crowd the rest out. */
const MAX_PER_GROUP = 6;

/**
 * Fuse options. `ignoreLocation` matters most: without it a match late in a long
 * keyword list scores far worse than the same match early, which would make an
 * item's ranking depend on the order someone happened to type its synonyms in.
 */
const FUSE_OPTIONS: IFuseOptions<PaletteItem> = {
  includeScore: true,
  ignoreLocation: true,
  threshold: 0.35,
  keys: [
    { name: "label", weight: 3 },
    { name: "keywords", weight: 1 },
  ],
};

/** The item's searchable words: its label plus every synonym. */
const searchableWords = (item: PaletteItem): string[] => toWords([item.label, ...(item.keywords ?? [])].join(" "));

/** Whether every meaningful token matches, allowing prefixes and small typos. */
const matchesEveryToken = (item: PaletteItem, tokens: string[]): boolean => {
  const words = searchableWords(item);
  return tokens.every((token) => tokenMatches(words, token));
};

/** Whether the label starts with the query. Always the strongest signal. */
const isPrefixMatch = (item: PaletteItem, query: string): boolean =>
  item.label.toLowerCase().startsWith(query.toLowerCase().trim());

/**
 * Ranks palette items for a query.
 *
 * Replaces cmdk's built-in filter, which is an ordered subsequence match over
 * one concatenated string and is wrong in both directions: it matched "invite"
 * against "Search hotels" by threading those letters through its keyword list,
 * and it found nothing for "nightly rate" because those two words appear in the
 * other order. Both were reproduced against the live palette on 2026-09-03.
 *
 * The fix is per-token matching plus fuzzy scoring:
 *  1. every meaningful query token must match one of the item's words, by
 *     prefix or within a small edit distance (kills the false positives, makes
 *     word order irrelevant, and tolerates a typo or a filler verb),
 *  2. survivors are ordered by Fuse score, with a label prefix always winning,
 *  3. each group is capped.
 *
 * @param items - Every candidate item, already filtered for scope and access.
 * @param query - The raw query string. Empty returns `items` untouched.
 * @returns Ranked items, best first, capped per group.
 */
export function rankItems(items: PaletteItem[], query: string): PaletteItem[] {
  const tokens = meaningfulTokens(query);
  // No cap at rest. The cap exists to stop one group flooding a *search* result;
  // applying it to the resting list silently hides most of the product, which is
  // the exact failure this feature was built to fix. The list scrolls.
  if (tokens.length === 0) return items;

  const candidates = items.filter((item) => matchesEveryToken(item, tokens));
  if (candidates.length === 0) return [];

  const fuse = new Fuse(candidates, FUSE_OPTIONS);
  const scored = new Map(fuse.search(query).map((result) => [result.item.id, result.score ?? 1]));

  const ranked = [...candidates].sort((a, b) => {
    const prefixDelta = Number(isPrefixMatch(b, query)) - Number(isPrefixMatch(a, query));
    if (prefixDelta !== 0) return prefixDelta;
    // Items that survived token matching but that Fuse did not score sort last
    // among their peers rather than jumping to the front on a 0 default.
    return (scored.get(a.id) ?? 1) - (scored.get(b.id) ?? 1);
  });

  return capPerGroup(ranked);
}

/** Keeps at most {@link MAX_PER_GROUP} items per group, preserving order. */
function capPerGroup(items: PaletteItem[]): PaletteItem[] {
  const counts = new Map<string, number>();
  return items.filter((item) => {
    const next = (counts.get(item.group) ?? 0) + 1;
    counts.set(item.group, next);
    return next <= MAX_PER_GROUP;
  });
}
